import type { CSSProperties, ReactNode } from "react";
import { AdaptiveEditorCard } from "./AdaptiveEditorCard";

function cx(...values: Array<unknown>): string {
  return values.filter((value): value is string => typeof value === "string" && value.length > 0).join(" ");
}

export function Page({ children, className, density = "comfortable" }: {
  children: ReactNode;
  className?: string;
  density?: "compact" | "comfortable";
}) {
  return <div className={cx("cd-page", className)} data-density={density}>{children}</div>;
}

export function PageHeader({ eyebrow, title, description, actions, className, titleId }: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  titleId?: string;
}) {
  return <header className={cx("cd-page-header", !className && "page-heading", actions && "has-actions", className)}>
    <div className="cd-page-header-copy">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 id={titleId}>{title}</h1>
      {description && <p className="cd-page-header-description">{description}</p>}
    </div>
    {actions && <div className="cd-page-header-actions">{actions}</div>}
  </header>;
}

export function Section({ children, className, labelledBy }: {
  children: ReactNode;
  className?: string;
  labelledBy?: string;
}) {
  return <section className={cx("cd-section", className)} aria-labelledby={labelledBy}>{children}</section>;
}

export function SectionHeader({ eyebrow, title, description, actions, titleId, className }: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  titleId?: string;
  className?: string;
}) {
  return <div className={cx("cd-section-header", !className && "section-heading", actions && "has-actions", className)}>
    <div>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2 id={titleId}>{title}</h2>
      {description && <p className="cd-section-header-description">{description}</p>}
    </div>
    {actions && <div className="cd-section-header-actions">{actions}</div>}
  </div>;
}

export function AdaptiveGrid({ children, compact = 1, medium = 2, wide = 3, className }: {
  children: ReactNode;
  compact?: number;
  medium?: number;
  wide?: number;
  className?: string;
}) {
  const style = {
    "--cd-grid-compact": String(compact),
    "--cd-grid-medium": String(medium),
    "--cd-grid-wide": String(wide),
  } as CSSProperties;
  return <div className={cx("cd-adaptive-grid", className)} style={style}>{children}</div>;
}

export interface ProductSubnavItem {
  key: string;
  label: ReactNode;
  href?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export function ProductSubnav({ items, activeKey, onNavigate, onSelect, className, itemClassName, ariaLabel = "Navegación del producto" }: {
  items: readonly ProductSubnavItem[];
  activeKey: string;
  onNavigate?: (path: string) => void;
  onSelect?: (key: string) => void;
  className?: string;
  itemClassName?: string | ((item: ProductSubnavItem, active: boolean) => string);
  ariaLabel?: string;
}) {
  return <nav className={cx("cd-product-subnav", className)} aria-label={ariaLabel} data-nav-kind="product">
    {items.map((item) => {
      const active = activeKey === item.key;
      const resolvedClassName = typeof itemClassName === "function" ? itemClassName(item, active) : itemClassName;
      const content = <>{item.icon && <span className="cd-product-subnav-icon" aria-hidden="true">{item.icon}</span>}<span>{item.label}</span></>;
      if (item.href) {
        return <a
          key={item.key}
          href={item.href}
          className={cx("cd-product-subnav-item", active && "active", resolvedClassName)}
          aria-current={active ? "page" : undefined}
          aria-disabled={item.disabled || undefined}
          onClick={(event) => {
            if (item.disabled) { event.preventDefault(); return; }
            if (onNavigate) { event.preventDefault(); onNavigate(item.href!); }
          }}
        >{content}</a>;
      }
      return <button
        key={item.key}
        type="button"
        className={cx("cd-product-subnav-item", active && "active", resolvedClassName)}
        aria-pressed={active}
        disabled={item.disabled}
        onClick={() => onSelect?.(item.key)}
      >{content}</button>;
    })}
  </nav>;
}

export function FormLayout({ children, className, columns = "auto" }: {
  children: ReactNode;
  className?: string;
  columns?: 1 | 2 | 3 | "auto";
}) {
  return <div className={cx("cd-form-layout", className)} data-columns={columns}>{children}</div>;
}

export function FieldGroup({ children, className, span = "auto" }: {
  children: ReactNode;
  className?: string;
  span?: 1 | 2 | 3 | "full" | "auto";
}) {
  return <div className={cx("cd-field-group", className)} data-span={span}>{children}</div>;
}

export function ActionBar({ children, className, sticky = "compact", align = "end" }: {
  children: ReactNode;
  className?: string;
  sticky?: "never" | "compact" | "always";
  align?: "start" | "between" | "end";
}) {
  return <div className={cx("cd-action-bar", className)} data-sticky={sticky} data-align={align}>{children}</div>;
}

export interface DetailsListItem {
  key: string;
  term: ReactNode;
  detail: ReactNode;
}

export function DetailsList({ items, className }: { items: readonly DetailsListItem[]; className?: string }) {
  return <dl className={cx("cd-details-list", className)}>{items.map((item) => <div key={item.key}><dt>{item.term}</dt><dd>{item.detail}</dd></div>)}</dl>;
}

export function StatusState({ eyebrow, title, message, action, meta, tone = "neutral", className, live = "polite" }: {
  eyebrow?: ReactNode;
  title: ReactNode;
  message?: ReactNode;
  action?: ReactNode;
  meta?: ReactNode;
  tone?: "neutral" | "loading" | "success" | "warning" | "danger";
  className?: string;
  live?: "off" | "polite" | "assertive";
}) {
  return <div className={cx("cd-status-state", className)} data-tone={tone} aria-live={live} role={tone === "danger" ? "alert" : undefined}>
    <div className="cd-status-state-symbol" aria-hidden="true" />
    <div className="cd-status-state-copy">
      {eyebrow && <span className="boundary-label">{eyebrow}</span>}
      <h2>{title}</h2>
      {message && <p>{message}</p>}
      {meta && <small>{meta}</small>}
      {action && <div className="cd-status-state-actions">{action}</div>}
    </div>
  </div>;
}

export interface CollectionEditorSummary {
  eyebrow?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  status?: ReactNode;
}

export function CollectionEditor<T>({
  items,
  getKey,
  renderSummary,
  renderEditor,
  defaultCompactOpen,
  compact = 1,
  medium = 2,
  wide = 3,
  className,
  gridClassName,
  itemClassName,
  empty,
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  renderSummary: (item: T) => CollectionEditorSummary;
  renderEditor: (item: T) => ReactNode;
  defaultCompactOpen?: (item: T) => boolean;
  compact?: number;
  medium?: number;
  wide?: number;
  className?: string;
  gridClassName?: string;
  itemClassName?: string;
  empty?: ReactNode;
}) {
  if (items.length === 0) return empty ? <>{empty}</> : null;
  const style = {
    "--cd-grid-compact": String(compact),
    "--cd-grid-medium": String(medium),
    "--cd-grid-wide": String(wide),
  } as CSSProperties;
  return <div className={cx("cd-collection-editor", className)}>
    <div className={cx("cd-collection-editor-grid", gridClassName)} style={style}>
      {items.map((item) => {
        const summary = renderSummary(item);
        return <AdaptiveEditorCard
          key={getKey(item)}
          className={cx("cd-collection-editor-item", itemClassName)}
          eyebrow={summary.eyebrow}
          title={summary.title}
          meta={summary.meta}
          status={summary.status}
          defaultCompactOpen={defaultCompactOpen?.(item) ?? false}
        >{renderEditor(item)}</AdaptiveEditorCard>;
      })}
    </div>
  </div>;
}
